import { useCallback, useState } from "react";
import {
  accountService,
  homeService,
  subscriptionService,
  taskService,
} from "@miplatform/api";
import { HTTP_STATUSES, PRINT_ORIENTATION } from "@miplatform/constants";
import { usePrint } from "@miplatform/hooks";
import { bugsnag } from "@miplatform/utils";
import { useSession } from "../../hooks";
import {
  ORDERS_ITEMS_PRINT_HEADER_FIELDS,
  ORDERS_ITEMS_PRINT_HIGHLIGHTED_HEADER_FIELD,
  ORDERS_ITEMS_PRINT_SECTIONS,
} from "../../views/Orders/ordersConstants";

export const useOrderActivityDrawerViewModel = ({
  open,
  order,
  getOrderActivity,
  onClose,
}) => {
  const [orderActivities, setOrderActivities] = useState([]);
  const [currentTab, setCurrentTab] = useState("orderDetails");
  const [tenant, setTenant] = useState(null);
  const [account, setAccount] = useState(null);
  const [homeDetails, setHomeDetails] = useState(null);

  const {
    session: { tenantId },
  } = useSession();

  const { handlePrintSectionsPages } = usePrint(tenant);

  const { home = {} } = order;
  const homeId = home?.id;
  const builderId = home?.builderId;

  const fetchTenantAccount = useCallback(async () => {
    if (open && tenantId && !tenant) {
      try {
        const response = await subscriptionService.subscriptions.get(tenantId);

        if (response.status === HTTP_STATUSES.OK) {
          setTenant(response.data);
        }
      } catch (error) {
        bugsnag.notify(error);
      }
    }
  }, [open, tenantId]);

  const fetchActivities = useCallback(async () => {
    if (open && order?.id) {
      try {
        const { data } = await getOrderActivity(order.id);

        const sortedData = data.sort(
          (a, b) =>
            new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
        );
        setOrderActivities(sortedData);
      } catch (error) {
        bugsnag.notify(error);
      }
    }
  }, [open, order?.id, getOrderActivity]);

  const fetchHomeDetails = useCallback(async () => {
    if (open && homeId) {
      try {
        const response = await homeService.homes.getById({
          id: homeId,
        });

        if (response.status === HTTP_STATUSES.OK) {
          setHomeDetails(response.data);
        }
      } catch (error) {
        bugsnag.notify(error);
      }
    }
  }, [open, homeId]);

  const handlePrint = useCallback(async () => {
    let currentAccount = account;

    if (!currentAccount && builderId) {
      try {
        const response = await accountService.accounts.getAccountById({
          id: builderId,
        });
        if (response.status === HTTP_STATUSES.OK) {
          currentAccount = response.data;
          setAccount(currentAccount);
        }
      } catch (error) {
        bugsnag.notify(error);
      }
    }

    const printData = {
      ...order,
      builderAddress: currentAccount?.address,
    };

    handlePrintSectionsPages(printData, ORDERS_ITEMS_PRINT_SECTIONS, {
      headerFields: ORDERS_ITEMS_PRINT_HEADER_FIELDS,
      highlightedHeaderField: ORDERS_ITEMS_PRINT_HIGHLIGHTED_HEADER_FIELD,
      orientation: PRINT_ORIENTATION.PORTRAIT,
    });
  }, [account, builderId, order, handlePrintSectionsPages]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const reset = useCallback(() => {
    setOrderActivities([]);
    setAccount(null);
    setHomeDetails(null);
    setCurrentTab("orderDetails");
  }, []);

  const currentHome = homeDetails || home;

  const updateOrder = async ({
    data,
    orderId,
    orderModifiedOn,
    onRefresh = () => {},
  }) => {
    const res = await taskService.order.update({
      orderId,
      data: {
        ...data,
        modifiedOn: orderModifiedOn,
      },
      toastMessageConfig: {
        success: {
          text: "Order details were updated",
        },
      },
    });

    if (res.status === HTTP_STATUSES.OK) {
      onRefresh(res.data);
    }
  };

  return {
    orderActivities,
    currentTab,
    setCurrentTab,
    currentHome,
    handlePrint,
    handleClose,
    fetchTenantAccount,
    fetchActivities,
    fetchHomeDetails,
    reset,
    updateOrder,
  };
};
